import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@pikoloo/darwin-ui/button";
import { Modal } from "@pikoloo/darwin-ui/modal";
import { useReactFlow } from "@xyflow/react";
import { AlertTriangle, CheckCircle2, Database, HardDrive, Keyboard, Magnet, RotateCcw, ShieldCheck, Square, TextSelect } from "lucide-react";
import { componentRegistry } from "./core/component-registry";
import {
  COMPONENT_SNAP_GAP,
  COMPONENT_SNAP_GAP_MAX,
  COMPONENT_SNAP_GAP_MIN,
} from "./core/node-snapping";
import {
  DEFAULT_NODE_RADIUS,
  NODE_RADIUS_MAX,
  NODE_RADIUS_MIN,
  clampNodeRadius,
} from "./core/node-appearance";
import { kimnoteDb } from "./db/database";
import { BoardCanvas } from "./features/canvas/BoardCanvas";
import { Titlebar, type ThemeMode } from "./features/shell/Titlebar";
import { Sidebar } from "./features/shell/Sidebar";
import { ComponentToolbar } from "./features/shell/ComponentToolbar";
import { Inspector } from "./features/shell/Inspector";
import { useEditorStore } from "./store/editor-store";

interface ToastMessage {
  id: string;
  text: string;
  kind: "success" | "error";
}

interface StorageInfo {
  usage: number;
  quota: number;
  persisted: boolean;
}

interface BoardActionTarget {
  id: string;
  title: string;
}

type BoardMoveRelation = "child" | "parent";

const SNAP_GAP_SETTING_KEY = "canvas.snapGap";
const NODE_RADIUS_SETTING_KEY = "canvas.defaultNodeRadius";
const NOTE_SELECTION_TOOLTIP_SETTING_KEY = "editor.noteSelectionTooltipEnabled";
const LEFT_SIDEBAR_SETTING_KEY = "kimnote.layout.leftSidebarVisible";
const RIGHT_SIDEBAR_SETTING_KEY = "kimnote.layout.rightSidebarVisible";

function initialTheme(): ThemeMode {
  const stored = localStorage.getItem("kimnote.theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function initialPanelVisibility(key: string): boolean {
  return localStorage.getItem(key) !== "false";
}

export default function App() {
  const status = useEditorStore((state) => state.status);
  const error = useEditorStore((state) => state.error);
  const project = useEditorStore((state) => state.project);
  const board = useEditorStore((state) => state.board);
  const projects = useEditorStore((state) => state.projects);
  const boards = useEditorStore((state) => state.boards);
  const nodes = useEditorStore((state) => state.nodes);
  const edges = useEditorStore((state) => state.edges);
  const saving = useEditorStore((state) => state.saving);
  const past = useEditorStore((state) => state.past);
  const future = useEditorStore((state) => state.future);
  const boot = useEditorStore((state) => state.boot);
  const createProject = useEditorStore((state) => state.createProject);
  const selectProject = useEditorStore((state) => state.selectProject);
  const renameProject = useEditorStore((state) => state.renameProject);
  const createBoard = useEditorStore((state) => state.createBoard);
  const selectBoard = useEditorStore((state) => state.selectBoard);
  const renameBoard = useEditorStore((state) => state.renameBoard);
  const moveBoard = useEditorStore((state) => state.moveBoard);
  const duplicateBoard = useEditorStore((state) => state.duplicateBoard);
  const deleteBoard = useEditorStore((state) => state.deleteBoard);
  const addNode = useEditorStore((state) => state.addNode);
  const addFiles = useEditorStore((state) => state.addFiles);
  const updateNodeData = useEditorStore((state) => state.updateNodeData);
  const updateEdge = useEditorStore((state) => state.updateEdge);
  const duplicateSelected = useEditorStore((state) => state.duplicateSelected);
  const deleteSelected = useEditorStore((state) => state.deleteSelected);
  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);
  const flush = useEditorStore((state) => state.flush);
  const refreshLibrary = useEditorStore((state) => state.refreshLibrary);
  const { screenToFlowPosition } = useReactFlow();

  const [theme, setTheme] = useState<ThemeMode>(initialTheme);
  const [query, setQuery] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<"help" | "settings" | null>(null);
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [snapGap, setSnapGap] = useState(COMPONENT_SNAP_GAP);
  const [defaultNodeRadius, setDefaultNodeRadius] = useState(DEFAULT_NODE_RADIUS);
  const [noteSelectionTooltipEnabled, setNoteSelectionTooltipEnabled] = useState(true);
  const [leftSidebarVisible, setLeftSidebarVisible] = useState(() => initialPanelVisibility(LEFT_SIDEBAR_SETTING_KEY));
  const [rightSidebarVisible, setRightSidebarVisible] = useState(() => initialPanelVisibility(RIGHT_SIDEBAR_SETTING_KEY));
  const [boardMoveTarget, setBoardMoveTarget] = useState<BoardActionTarget | null>(null);
  const [boardMoveRelativeId, setBoardMoveRelativeId] = useState("");
  const [boardMoveRelation, setBoardMoveRelation] = useState<BoardMoveRelation>("child");
  const [boardDeleteTarget, setBoardDeleteTarget] = useState<BoardActionTarget | null>(null);
  const snapGapLoaded = useRef(false);
  const nodeRadiusLoaded = useRef(false);
  const noteSelectionTooltipLoaded = useRef(false);
  const importInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => { void boot(); }, [boot]);
  useEffect(() => {
    let active = true;
    void kimnoteDb.settings.get(SNAP_GAP_SETTING_KEY).then((record) => {
      if (!active) return;
      const stored = Number(record?.value);
      if (Number.isFinite(stored)) {
        setSnapGap(Math.round(Math.max(COMPONENT_SNAP_GAP_MIN, Math.min(COMPONENT_SNAP_GAP_MAX, stored))));
      }
      snapGapLoaded.current = true;
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    void kimnoteDb.settings.get(NODE_RADIUS_SETTING_KEY).then((record) => {
      if (!active) return;
      const stored = Number(record?.value);
      if (Number.isFinite(stored)) setDefaultNodeRadius(clampNodeRadius(stored));
      nodeRadiusLoaded.current = true;
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    void kimnoteDb.settings.get(NOTE_SELECTION_TOOLTIP_SETTING_KEY).then((record) => {
      if (!active) return;
      if (typeof record?.value === "boolean") setNoteSelectionTooltipEnabled(record.value);
      noteSelectionTooltipLoaded.current = true;
    });
    return () => { active = false; };
  }, []);
  useEffect(() => { setTitleDraft(project?.title ?? ""); }, [project?.id, project?.title]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("kimnote.theme", theme);
  }, [theme]);
  useEffect(() => {
    localStorage.setItem(LEFT_SIDEBAR_SETTING_KEY, String(leftSidebarVisible));
  }, [leftSidebarVisible]);
  useEffect(() => {
    localStorage.setItem(RIGHT_SIDEBAR_SETTING_KEY, String(rightSidebarVisible));
  }, [rightSidebarVisible]);

  useEffect(() => {
    if (modal !== "settings") return;
    void readStorageInfo().then(setStorageInfo);
  }, [modal]);

  const selectedNodes = useMemo(() => nodes.filter((node) => node.selected), [nodes]);
  const selectedEdges = useMemo(() => edges.filter((edge) => edge.selected), [edges]);
  const selectionCount = selectedNodes.length + selectedEdges.length;
  const plugins = useMemo(
    () => componentRegistry.list().filter((plugin) => plugin.declarations.availability?.creatable !== false),
    [],
  );

  const canvasPlacement = (kind = "note") => {
    const canvas = document.querySelector<HTMLElement>(".canvas-region");
    const bounds = canvas?.getBoundingClientRect();
    const center = bounds
      ? screenToFlowPosition({ x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 })
      : { x: 120, y: 100 };
    const size = componentRegistry.get(kind)?.defaultSize ?? { width: 240, height: 180 };
    const base = { x: center.x - size.width / 2, y: center.y - size.height / 2 };
    if (!bounds) return base;

    const cornerA = screenToFlowPosition({ x: bounds.left + 22, y: bounds.top + 22 });
    const cornerB = screenToFlowPosition({ x: bounds.right - 22, y: bounds.bottom - 22 });
    const minX = Math.min(cornerA.x, cornerB.x);
    const minY = Math.min(cornerA.y, cornerB.y);
    const maxX = Math.max(minX, Math.max(cornerA.x, cornerB.x) - size.width);
    const maxY = Math.max(minY, Math.max(cornerA.y, cornerB.y) - size.height);
    const nearbyNodes = nodes.filter((node) => {
      const nodeWidth = node.measured?.width ?? node.width ?? 240;
      const nodeHeight = node.measured?.height ?? node.height ?? 160;
      return node.position.x + nodeWidth >= minX - 40 && node.position.x <= maxX + size.width + 40 &&
        node.position.y + nodeHeight >= minY - 40 && node.position.y <= maxY + size.height + 40;
    });
    const step = 34;
    const candidates: Array<{ x: number; y: number }> = [];
    for (let y = minY; y <= maxY + 1; y += step) {
      for (let x = minX; x <= maxX + 1; x += step) candidates.push({ x: Math.round(x), y: Math.round(y) });
    }
    candidates.push({
      x: Math.round(Math.max(minX, Math.min(maxX, base.x))),
      y: Math.round(Math.max(minY, Math.min(maxY, base.y))),
    });
    candidates.sort((left, right) =>
      (left.x - base.x) ** 2 + (left.y - base.y) ** 2 - ((right.x - base.x) ** 2 + (right.y - base.y) ** 2),
    );

    let best = candidates[0] ?? base;
    let bestScore = Number.POSITIVE_INFINITY;
    for (const candidate of candidates) {
      let overlapArea = 0;
      for (const node of nearbyNodes) {
        const fallback = componentRegistry.get(node.data.kind)?.defaultSize ?? { width: 240, height: 160 };
        const nodeWidth = node.measured?.width ?? node.width ?? fallback.width;
        const nodeHeight = node.measured?.height ?? node.height ?? fallback.height;
        const margin = 18;
        const overlapWidth = Math.min(candidate.x + size.width + margin, node.position.x + nodeWidth) - Math.max(candidate.x - margin, node.position.x);
        const overlapHeight = Math.min(candidate.y + size.height + margin, node.position.y + nodeHeight) - Math.max(candidate.y - margin, node.position.y);
        if (overlapWidth > 0 && overlapHeight > 0) overlapArea += overlapWidth * overlapHeight;
      }
      if (overlapArea === 0) return candidate;
      const distancePenalty = Math.hypot(candidate.x - base.x, candidate.y - base.y) * 0.25;
      const score = overlapArea + distancePenalty;
      if (score < bestScore) {
        best = candidate;
        bestScore = score;
      }
    }
    return best;
  };

  const addToast = (text: string, kind: ToastMessage["kind"] = "success") => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { id, text, kind }]);
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 3200);
  };

  const handleInsert = (type: string) => {
    if (type === "image") {
      imageInput.current?.click();
      return;
    }
    if (type === "file") {
      fileInput.current?.click();
      return;
    }
    addNode(type, canvasPlacement(type));
  };

  const handleMoveBoard = (boardId: string, parentId: string | null) => {
    void moveBoard(boardId, parentId).catch((moveError) => {
      addToast(moveError instanceof Error ? moveError.message : "无法修改画板层级", "error");
    });
  };

  const handleDuplicateBoard = (boardId: string) => {
    void duplicateBoard(boardId)
      .then((copy) => { if (copy) addToast(`已复制「${copy.title}」及全部子画板`); })
      .catch((copyError) => {
        addToast(copyError instanceof Error ? copyError.message : "无法复制画板", "error");
      });
  };

  const requestMoveBoard = (boardId: string) => {
    const moving = boards.find((candidate) => candidate.id === boardId);
    const relative = boards.find((candidate) => candidate.id !== boardId);
    if (!moving || !relative) {
      addToast("至少需要两个画板才能设置父子关系", "error");
      return;
    }
    setBoardMoveTarget({ id: moving.id, title: moving.title });
    setBoardMoveRelativeId(relative.id);
    setBoardMoveRelation("child");
  };

  const confirmMoveBoard = async () => {
    if (!boardMoveTarget || !boardMoveRelativeId) return;
    setBusy(true);
    try {
      if (boardMoveRelation === "child") {
        await moveBoard(boardMoveTarget.id, boardMoveRelativeId);
      } else {
        await moveBoard(boardMoveRelativeId, boardMoveTarget.id);
      }
      const relative = boards.find((candidate) => candidate.id === boardMoveRelativeId);
      addToast(boardMoveRelation === "child"
        ? `已将「${boardMoveTarget.title}」设为「${relative?.title ?? "所选画板"}」的子级`
        : `已将「${boardMoveTarget.title}」设为「${relative?.title ?? "所选画板"}」的父级`);
      setBoardMoveTarget(null);
    } catch (moveError) {
      addToast(moveError instanceof Error ? moveError.message : "无法修改画板层级", "error");
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteBoard = async () => {
    if (!boardDeleteTarget) return;
    setBusy(true);
    try {
      await deleteBoard(boardDeleteTarget.id);
      addToast(`已删除画板「${boardDeleteTarget.title}」`);
      setBoardDeleteTarget(null);
    } catch (deleteError) {
      addToast(deleteError instanceof Error ? deleteError.message : "无法删除画板", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleAssetInput = async (files: FileList | null, kind: "image" | "file") => {
    if (!files?.length) return;
    setBusy(true);
    try {
      await addFiles([...files], canvasPlacement(kind));
      addToast(`已添加 ${files.length} 个本地文件`);
    } catch (assetError) {
      addToast(assetError instanceof Error ? assetError.message : "文件添加失败", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (!project) return;
    setBusy(true);
    try {
      await flush();
      const { exportProjectPackage } = await import("./features/io");
      const blob = await exportProjectPackage(project.id);
      downloadBlob(blob, `${safeFilename(project.title)}.kimnote`);
      addToast("项目包已导出");
    } catch (exportError) {
      addToast(exportError instanceof Error ? exportError.message : "导出失败", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      await flush();
      const { importProjectPackage } = await import("./features/io");
      const result = await importProjectPackage(file);
      await refreshLibrary();
      await selectProject(result.project.id);
      addToast(result.remapped ? "项目已作为新副本导入" : "项目导入完成");
    } catch (importError) {
      addToast(importError instanceof Error ? importError.message : "项目包无效", "error");
    } finally {
      setBusy(false);
      if (importInput.current) importInput.current.value = "";
    }
  };

  const requestPersistentStorage = async () => {
    if (!navigator.storage?.persist) return;
    const persisted = await navigator.storage.persist();
    setStorageInfo(await readStorageInfo());
    addToast(persisted ? "浏览器已允许持久存储" : "浏览器暂未授予持久存储", persisted ? "success" : "error");
  };

  const persistSnapGap = (value = snapGap) => {
    if (!snapGapLoaded.current) return;
    void kimnoteDb.settings.put({ key: SNAP_GAP_SETTING_KEY, value });
  };

  const persistDefaultNodeRadius = (value = defaultNodeRadius) => {
    if (!nodeRadiusLoaded.current) return;
    void kimnoteDb.settings.put({ key: NODE_RADIUS_SETTING_KEY, value: clampNodeRadius(value) });
  };

  const persistNoteSelectionTooltip = (value = noteSelectionTooltipEnabled) => {
    if (!noteSelectionTooltipLoaded.current) return;
    void kimnoteDb.settings.put({ key: NOTE_SELECTION_TOOLTIP_SETTING_KEY, value });
  };

  if (status === "idle" || status === "loading") {
    return (
      <main className="loading-screen">
        <div className="loading-card"><div className="loading-spinner" /><span>正在打开本地工作区…</span></div>
      </main>
    );
  }

  if (status === "error" || !project || !board) {
    return (
      <main className="loading-screen">
        <div className="loading-card">
          <AlertTriangle color="var(--danger)" />
          <strong>无法打开 Kimnote</strong>
          <span>{error ?? "本地项目不可用"}</span>
          <Button variant="primary" onClick={() => void boot()}>重试</Button>
        </div>
      </main>
    );
  }

  return (
    <div className="desktop-stage">
      <section
        className="app-window"
        aria-label="Kimnote 应用"
        onBlurCapture={(event) => {
          if ((event.target as HTMLElement).matches("input, textarea, [contenteditable='true']")) void flush();
        }}
      >
        <Titlebar
          projectTitle={titleDraft}
          onProjectTitleChange={setTitleDraft}
          onProjectTitleCommit={(value) => renameProject(value || "未命名项目")}
          onUndo={undo}
          onRedo={redo}
          canUndo={past.length > 0}
          canRedo={future.length > 0}
          onImport={() => importInput.current?.click()}
          onExport={() => void handleExport()}
          theme={theme}
          onToggleTheme={() => setTheme((current) => current === "light" ? "dark" : "light")}
          leftSidebarVisible={leftSidebarVisible}
          rightSidebarVisible={rightSidebarVisible}
          onToggleLeftSidebar={() => setLeftSidebarVisible((visible) => !visible)}
          onToggleRightSidebar={() => setRightSidebarVisible((visible) => !visible)}
          saveStatus={error ? "error" : saving ? "saving" : "saved"}
          disabled={busy}
        />

        <div className={`app-body${leftSidebarVisible ? "" : " is-sidebar-collapsed"}${rightSidebarVisible ? "" : " is-inspector-collapsed"}`}>
          {leftSidebarVisible ? <Sidebar
            projects={projects.map((item) => ({ id: item.id, title: item.title }))}
            boards={boards.map((item) => ({
              id: item.id,
              title: item.title,
              projectId: item.projectId,
              parentId: item.parentId,
              nodeCount: item.id === board.id ? nodes.length : undefined,
            }))}
            activeProjectId={project.id}
            activeBoardId={board.id}
            query={query}
            onQueryChange={setQuery}
            onSelectProject={(id) => void selectProject(id)}
            onSelectBoard={(id) => void selectBoard(id)}
            onMoveBoard={handleMoveBoard}
            onDuplicateBoard={handleDuplicateBoard}
            onRequestMoveBoard={requestMoveBoard}
            onRequestDeleteBoard={(id) => {
              const target = boards.find((candidate) => candidate.id === id);
              if (target) setBoardDeleteTarget({ id: target.id, title: target.title });
            }}
            onCreateProject={() => void createProject()}
            onCreateBoard={() => void createBoard()}
            onImportProject={() => importInput.current?.click()}
            onOpenSettings={() => setModal("settings")}
            onOpenHelp={() => setModal("help")}
            disabled={busy}
          /> : null}
          <ComponentToolbar plugins={plugins} onInsert={handleInsert} disabled={busy} label="组件" />
          <BoardCanvas
            snapGap={snapGap}
            defaultNodeRadius={defaultNodeRadius}
            noteSelectionTooltipEnabled={noteSelectionTooltipEnabled}
          />
          {rightSidebarVisible ? <Inspector
            selectedNode={selectedNodes[0]}
            selectedEdge={selectedEdges[0]}
            selectionCount={selectionCount}
            board={board}
            boards={boards}
            onUpdateNode={(id, patch) => updateNodeData(id, patch, "调整组件")}
            onUpdateEdge={updateEdge}
            onDuplicateNode={() => duplicateSelected()}
            onMoveBoard={handleMoveBoard}
            onDuplicateBoard={handleDuplicateBoard}
            onRenameBoard={(title) => renameBoard(title || "未命名画板")}
            onDeleteSelection={deleteSelected}
            defaultNodeRadius={defaultNodeRadius}
            disabled={busy}
          /> : null}
        </div>
      </section>

      <input ref={importInput} className="visually-hidden" type="file" accept=".kimnote,application/zip" onChange={(event) => void handleImport(event.target.files?.[0])} />
      <input ref={imageInput} className="visually-hidden" type="file" accept="image/*" multiple onChange={(event) => { void handleAssetInput(event.target.files, "image"); event.currentTarget.value = ""; }} />
      <input ref={fileInput} className="visually-hidden" type="file" multiple onChange={(event) => { void handleAssetInput(event.target.files, "file"); event.currentTarget.value = ""; }} />

      <div className="toast-stack" aria-live="polite">
        {toasts.map((toast) => (
          <div className="app-toast" key={toast.id}>
            {toast.kind === "success" ? <CheckCircle2 size={16} color="var(--success)" /> : <AlertTriangle size={16} color="var(--danger)" />}
            {toast.text}
          </div>
        ))}
      </div>

      <Modal
        isOpen={modal === "settings"}
        onClose={() => { persistSnapGap(); persistDefaultNodeRadius(); persistNoteSelectionTooltip(); setModal(null); }}
        title="设置"
        size="lg"
        glass
        className="settings-modal"
      >
        <div className="settings-panel">
          <section className="settings-section" aria-labelledby="settings-canvas-title">
            <div className="settings-section__heading">
              <div>
                <h3 id="settings-canvas-title">画布</h3>
                <p>调整组件在自由画布上的排列手感。</p>
              </div>
            </div>
            <div className="settings-card">
              <div className="settings-row">
                <div className="settings-row__icon"><Magnet size={18} /></div>
                <div className="settings-row__body">
                  <div className="settings-row__title">
                    <label htmlFor="snap-gap-range">组件吸附间距</label>
                    <output htmlFor="snap-gap-range">{snapGap} px</output>
                  </div>
                  <p>靠近其他组件边缘时保留的空隙。数值越小，排列越紧凑。</p>
                  <div className="settings-range-line">
                    <input
                      id="snap-gap-range"
                      className="settings-range"
                      type="range"
                      min={COMPONENT_SNAP_GAP_MIN}
                      max={COMPONENT_SNAP_GAP_MAX}
                      step={1}
                      value={snapGap}
                      aria-valuetext={`${snapGap} 像素`}
                      onChange={(event) => setSnapGap(Number(event.currentTarget.value))}
                      onPointerUp={() => persistSnapGap()}
                      onKeyUp={() => persistSnapGap()}
                      onBlur={() => persistSnapGap()}
                    />
                    <button
                      type="button"
                      className="settings-reset-button"
                      onClick={() => { setSnapGap(COMPONENT_SNAP_GAP); persistSnapGap(COMPONENT_SNAP_GAP); }}
                      disabled={snapGap === COMPONENT_SNAP_GAP}
                      title={`恢复默认 ${COMPONENT_SNAP_GAP}px`}
                    >
                      <RotateCcw size={13} aria-hidden="true" />
                      默认
                    </button>
                  </div>
                  <div className="settings-range-labels"><span>紧凑</span><span>宽松</span></div>
                </div>
              </div>
              <div className="settings-row">
                <div className="settings-row__icon settings-row__icon--radius"><Square size={18} /></div>
                <div className="settings-row__body">
                  <div className="settings-row__title">
                    <label htmlFor="node-radius-range">组件默认圆角</label>
                    <output htmlFor="node-radius-range">{defaultNodeRadius} px</output>
                  </div>
                  <p>只影响没有单独设置圆角的组件；默认值不会写入组件数据。</p>
                  <div className="settings-range-line">
                    <input
                      id="node-radius-range"
                      className="settings-range"
                      type="range"
                      min={NODE_RADIUS_MIN}
                      max={NODE_RADIUS_MAX}
                      step={1}
                      value={defaultNodeRadius}
                      aria-valuetext={`${defaultNodeRadius} 像素`}
                      onChange={(event) => setDefaultNodeRadius(clampNodeRadius(Number(event.currentTarget.value)))}
                      onPointerUp={() => persistDefaultNodeRadius()}
                      onKeyUp={() => persistDefaultNodeRadius()}
                      onBlur={() => persistDefaultNodeRadius()}
                    />
                    <button
                      type="button"
                      className="settings-reset-button"
                      onClick={() => { setDefaultNodeRadius(DEFAULT_NODE_RADIUS); persistDefaultNodeRadius(DEFAULT_NODE_RADIUS); }}
                      disabled={defaultNodeRadius === DEFAULT_NODE_RADIUS}
                      title="恢复默认无圆角"
                    >
                      <RotateCcw size={13} aria-hidden="true" />
                      默认
                    </button>
                  </div>
                  <div className="settings-range-labels"><span>直角</span><span>圆润</span></div>
                </div>
              </div>
              <div className="settings-row">
                <div className="settings-row__icon settings-row__icon--editor"><TextSelect size={18} /></div>
                <div className="settings-row__body">
                  <div className="settings-row__title">
                    <label htmlFor="note-selection-tooltip-toggle">选中文字浮动格式工具</label>
                    <button
                      id="note-selection-tooltip-toggle"
                      type="button"
                      className="settings-switch"
                      role="switch"
                      aria-checked={noteSelectionTooltipEnabled}
                      onClick={() => {
                        const nextValue = !noteSelectionTooltipEnabled;
                        setNoteSelectionTooltipEnabled(nextValue);
                        persistNoteSelectionTooltip(nextValue);
                      }}
                    >
                      <span aria-hidden="true" />
                      <span className="visually-hidden">{noteSelectionTooltipEnabled ? "已启用" : "已禁用"}</span>
                    </button>
                  </div>
                  <p>便签小组件选中文字时，在选区上方显示格式选项；工具条上的关闭只对本次编辑生效。</p>
                </div>
              </div>
            </div>
          </section>

          <section className="settings-section" aria-labelledby="settings-storage-title">
            <div className="settings-section__heading">
              <div>
                <h3 id="settings-storage-title">本地数据</h3>
                <p>项目、画板和附件只保存在当前浏览器。</p>
              </div>
            </div>
            <div className="settings-card">
              <div className="settings-row">
                <div className="settings-row__icon settings-row__icon--storage"><Database size={18} /></div>
                <div className="settings-row__body">
                  <div className="settings-row__title"><strong>IndexedDB 存储</strong></div>
                  <p>实体增量写入；图片和文件以 SHA-256 内容哈希去重保存。</p>
                  {storageInfo ? (
                    <div className="storage-meter-wrap">
                      <div className="storage-meter-copy"><span>已使用 {formatBytes(storageInfo.usage)}</span><span>配额 {formatBytes(storageInfo.quota)}</span></div>
                      <div className="storage-meter"><span style={{ width: `${Math.min(100, storageInfo.quota ? storageInfo.usage / storageInfo.quota * 100 : 0)}%` }} /></div>
                      <div className="storage-persist-state">
                        {storageInfo.persisted ? <ShieldCheck size={15} color="var(--success)" /> : <HardDrive size={15} />}
                        <span>{storageInfo.persisted ? "已启用持久存储" : "建议申请持久存储，并定期导出项目包"}</span>
                        {!storageInfo.persisted ? (
                          <button type="button" className="settings-inline-action" onClick={() => void requestPersistentStorage()}>
                            申请持久存储
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : <div className="settings-loading-line">正在读取存储状态…</div>}
                </div>
              </div>
            </div>
          </section>

          <div className="modal-actions settings-actions">
            <Button variant="primary" onClick={() => { persistSnapGap(); persistDefaultNodeRadius(); persistNoteSelectionTooltip(); setModal(null); }}>完成</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={modal === "help"} onClose={() => setModal(null)} title="画布操作" size="md" glass>
        <div className="shortcut-list">
          <div><Keyboard size={16} /><span>双击空白处</span><strong>新建便签</strong></div>
          <div><kbd>Space</kbd><span>按住并拖动</span><strong>平移画布</strong></div>
          <div><kbd>⇧</kbd><span>拖动空白处</span><strong>框选多个组件</strong></div>
          <div><kbd>⌘D</kbd><span>复制所选</span><strong>快速创建副本</strong></div>
          <div><kbd>⌘0</kbd><span>缩放画布</span><strong>查看全部内容</strong></div>
          <div><kbd>⌫</kbd><span>删除所选</span><strong>支持撤销</strong></div>
        </div>
        <p className="modal-copy">组件靠近另一个组件的边缘时会自动吸附，并保留 {snapGap}px 画布间距；可在设置中调整。</p>
        <p className="modal-copy">每个组件四周都有连接点。将连接点拖到另一个组件，即可创建会随卡片移动的连线。</p>
        <div className="modal-actions"><Button variant="primary" onClick={() => setModal(null)}>知道了</Button></div>
      </Modal>

      <Modal
        isOpen={Boolean(boardMoveTarget)}
        onClose={() => { if (!busy) setBoardMoveTarget(null); }}
        title={`移动「${boardMoveTarget?.title ?? "画板"}」`}
        size="md"
        className="board-operation-modal"
      >
        <div className="board-operation-panel">
          <p className="modal-copy">选择一个画板，并决定当前画板与它的父子关系。内容和入口引用不会被复制或移动。</p>
          <fieldset className="board-relation-picker">
            <legend>层级关系</legend>
            <button
              type="button"
              className={boardMoveRelation === "child" ? "is-active" : ""}
              onClick={() => setBoardMoveRelation("child")}
              aria-pressed={boardMoveRelation === "child"}
            >
              设为所选画板的子级
            </button>
            <button
              type="button"
              className={boardMoveRelation === "parent" ? "is-active" : ""}
              onClick={() => setBoardMoveRelation("parent")}
              aria-pressed={boardMoveRelation === "parent"}
            >
              设为所选画板的父级
            </button>
          </fieldset>
          <label className="modal-field" htmlFor="board-move-relative">
            <span>选择画板</span>
            <select
              id="board-move-relative"
              value={boardMoveRelativeId}
              onChange={(event) => setBoardMoveRelativeId(event.currentTarget.value)}
              disabled={busy}
            >
              {boards.filter((candidate) => candidate.id !== boardMoveTarget?.id).map((candidate) => (
                <option key={candidate.id} value={candidate.id}>{candidate.title}</option>
              ))}
            </select>
          </label>
          <p className="board-operation-preview">
            {boardMoveRelation === "child"
              ? `「${boardMoveTarget?.title ?? "当前画板"}」将成为所选画板的子级。`
              : `所选画板将成为「${boardMoveTarget?.title ?? "当前画板"}」的子级。`}
          </p>
          <div className="modal-actions">
            <Button onClick={() => setBoardMoveTarget(null)} disabled={busy}>取消</Button>
            <Button variant="primary" onClick={() => void confirmMoveBoard()} disabled={busy || !boardMoveRelativeId}>确认移动</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(boardDeleteTarget)}
        onClose={() => { if (!busy) setBoardDeleteTarget(null); }}
        title="删除画板"
        size="md"
        className="board-operation-modal"
      >
        <div className="board-operation-panel board-delete-panel">
          <div className="board-delete-warning"><AlertTriangle size={20} aria-hidden="true" /></div>
          <div>
            <strong>确定删除「{boardDeleteTarget?.title}」？</strong>
            <p>该画板的全部组件和连线将被永久删除。它的子画板会提升一级，所有指向该画板的入口卡片也会一并移除。</p>
          </div>
          <div className="modal-actions board-delete-actions">
            <Button onClick={() => setBoardDeleteTarget(null)} disabled={busy}>取消</Button>
            <button type="button" className="modal-danger-button" onClick={() => void confirmDeleteBoard()} disabled={busy}>删除画板</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

async function readStorageInfo(): Promise<StorageInfo> {
  const estimate = await navigator.storage?.estimate?.();
  const persisted = await navigator.storage?.persisted?.();
  return { usage: estimate?.usage ?? 0, quota: estimate?.quota ?? 0, persisted: persisted ?? false };
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function safeFilename(value: string): string {
  return value.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_").trim() || "kimnote-project";
}

function formatBytes(value: number): string {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const unit = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** unit).toFixed(unit ? 1 : 0)} ${units[unit]}`;
}
